import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';

import { FRONTEND_PERMISSIONS } from '../../permissions.constants';
import { PermissionService } from '../../services/permission.service';

interface QuickLink {
  label: string;
  icon: string;
  link: string;
  permission?: string;
}

@Component({
  selector: 'app-quick-links-card',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ButtonModule],
  template: `
    <div class="quick-links-content">
      <div class="quick-search">
        <input
          type="text"
          pInputText
          [(ngModel)]="searchTerm"
          (keyup.enter)="doSearch()"
          placeholder="Search jobs, customers..."
          class="search-input"
        >
        <button
          pButton
          type="button"
          icon="pi pi-search"
          class="p-button-text search-button"
          (click)="doSearch()"
        ></button>
      </div>
      <div class="quick-links-list">
        <a
          *ngFor="let link of visibleLinks"
          class="quick-link"
          [routerLink]="link.link"
        >
          <i class="pi {{ link.icon }}"></i>
          <span>{{ link.label }}</span>
        </a>
      </div>
    </div>
  `,
  styles: [`
    .quick-links-content {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .quick-search {
      display: flex;
      align-items: center;
      border: 1px solid var(--surface-border);
      border-radius: 6px;
      overflow: hidden;
      background: var(--surface-ground);
      margin-bottom: 0.5rem;

      &:focus-within {
        border-color: var(--color-primary);
      }

      .search-input {
        flex: 1;
        border: none;
        background: transparent;
        padding: 0.5rem 0.75rem;
        font-size: 0.875rem;
        outline: none;
        width: 100%;
      }

      .search-button {
        flex-shrink: 0;
        border: none !important;
        background: transparent !important;
        padding: 0.5rem 0.625rem;
        cursor: pointer;
        color: var(--text-color-secondary);

        &:hover {
          color: var(--text-color);
        }
      }
    }
    .quick-links-list {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
    .quick-link {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.625rem 0.875rem;
      border-radius: 6px;
      text-decoration: none;
      color: var(--text-color);
      transition: background 0.15s;
    }
    .quick-link:hover {
      background: var(--surface-hover);
    }
    .quick-link i {
      font-size: 1rem;
      color: var(--text-color-secondary);
      width: 1.25rem;
      text-align: center;
    }
  `],
})
export class QuickLinksCardComponent {
  searchTerm = '';

  links: QuickLink[] = [
    { label: 'New Opportunity', icon: 'pi pi-plus', link: '/jobs/new' },
    { label: 'Schedule', icon: 'pi pi-calendar', link: '/schedule', permission: FRONTEND_PERMISSIONS.schedule },
    { label: 'Jobs', icon: 'pi pi-briefcase', link: '/jobs' },
    { label: 'People', icon: 'pi pi-users', link: '/people' },
    { label: 'Reports', icon: 'pi pi-chart-bar', link: '/reports' },
    { label: 'Business', icon: 'pi pi-building', link: '/business' },
  ];

  constructor(
    private router: Router,
    private permissionService: PermissionService,
  ) {}

  get visibleLinks(): QuickLink[] {
    return this.links.filter((l) => !l.permission || this.permissionService.checkPermission(l.permission));
  }

  doSearch(): void {
    if (this.searchTerm.trim()) {
      this.router.navigate(['/search'], { queryParams: { q: this.searchTerm.trim() } });
    }
  }
}